import axios from 'axios'
import { get } from 'es-toolkit/compat'

import defaultClientFeatureFlags from '@/config/clientFeatureFlags.json' with { type: 'json' }
import { FASTAPI_CONFIG } from '@/config/fastapi'
import type {
  ModelFile,
  ModelFolderInfo
} from '@/platform/assets/schemas/assetSchema'
import { useToastStore } from '@/platform/updates/common/toastStore'
import { type WorkflowTemplates } from '@/platform/workflow/templates/types/template'
import type {
  ComfyApiWorkflow,
  ComfyWorkflowJSON,
  NodeId
} from '@/platform/workflow/validation/schemas/workflowSchema'
import type {
  DisplayComponentWsMessage,
  EmbeddingsResponse,
  ExecutedWsMessage,
  ExecutingWsMessage,
  ExecutionCachedWsMessage,
  ExecutionErrorWsMessage,
  ExecutionInterruptedWsMessage,
  ExecutionStartWsMessage,
  ExecutionSuccessWsMessage,
  ExtensionsResponse,
  FeatureFlagsWsMessage,
  HistoryTaskItem,
  LogsRawResponse,
  LogsWsMessage,
  PendingTaskItem,
  ProgressStateWsMessage,
  ProgressTextWsMessage,
  ProgressWsMessage,
  PromptResponse,
  RunningTaskItem,
  Settings,
  StatusWsMessage,
  StatusWsMessageStatus,
  // TaskErrorMessage,
  // SystemStats,
  User,
  UserDataFullInfo
} from '@/schemas/apiSchema'
import type { ComfyNodeDef } from '@/schemas/nodeDefSchema'
import type { NodeExecutionId } from '@/types/nodeIdentification'

interface Server {
  id: number
  name: string
  base_url: string
  ws_url: string
  status: string
  max_concurrent_tasks?: number
  current_tasks?: number
  priority?: number
  health_check_url?: string
  health_status?: string
  last_health_check?: string
  system_stats?: any
  server?: number // 机器编号
  remark?: string // 备注
  gpu?: number // 显卡序号
  vram?: string // 显存
  object_info?: any
  capabilities?: string[]
  version?: string
  lastSeen?: string
  monitorData?: any
  lastUpdate?: Date
}

interface ServersResponse {
  servers: Server[]
}

interface QueuePromptRequestBody {
  client_id: string
  prompt: ComfyApiWorkflow
  partial_execution_targets?: NodeExecutionId[]
  workflows_id?: number // 添加工作流ID字段
  extra_data: {
    extra_pnginfo: {
      workflow: ComfyWorkflowJSON
    }
    /**
     * The auth token for the comfy org account if the user is logged in.
     *
     * Backend node can access this token by specifying following input:
     * ```python
      @classmethod
      def INPUT_TYPES(s):
        return {
          "hidden": { "auth_token": "AUTH_TOKEN_COMFY_ORG"}
        }

      def execute(self, auth_token: str):
        print(f"Auth token: {auth_token}")
     * ```
     */
    auth_token_comfy_org?: string
    /**
     * The auth token for the comfy org account if the user is logged in.
     *
     * Backend node can access this token by specifying following input:
     * ```python
     * def INPUT_TYPES(s):
     *   return {
     *     "hidden": { "api_key": "API_KEY_COMFY_ORG" }
     *   }
     *
     * def execute(self, api_key: str):
     *   print(f"API Key: {api_key}")
     * ```
     */
    api_key_comfy_org?: string
  }
  front?: boolean
  number?: number
}

/**
 * Options for queuePrompt method
 */
interface QueuePromptOptions {
  /**
   * Optional list of node execution IDs to execute (partial execution).
   * Each ID represents a node's position in nested subgraphs.
   * Format: Colon-separated path of node IDs (e.g., "123:456:789")
   */
  partialExecutionTargets?: NodeExecutionId[]
}

/** Dictionary of Frontend-generated API calls */
interface FrontendApiCalls {
  graphChanged: ComfyWorkflowJSON
  promptQueued: { number: number; batchCount: number }
  graphCleared: never
  reconnecting: never
  reconnected: never
}

/** Dictionary of calls originating from ComfyUI core */
interface BackendApiCalls {
  progress: ProgressWsMessage
  executing: ExecutingWsMessage
  executed: ExecutedWsMessage
  status: StatusWsMessage
  execution_start: ExecutionStartWsMessage
  execution_success: ExecutionSuccessWsMessage
  execution_error: ExecutionErrorWsMessage
  execution_interrupted: ExecutionInterruptedWsMessage
  execution_cached: ExecutionCachedWsMessage
  logs: LogsWsMessage
  /** Binary preview/progress data */
  b_preview: Blob
  /** Binary preview with metadata (node_id, prompt_id) */
  b_preview_with_metadata: {
    blob: Blob
    nodeId: string
    parentNodeId: string
    displayNodeId: string
    realNodeId: string
    promptId: string
  }
  progress_text: ProgressTextWsMessage
  progress_state: ProgressStateWsMessage
  display_component: DisplayComponentWsMessage
  feature_flags: FeatureFlagsWsMessage
  task_error: any // TODO 临时使用 any，稍后修复
  points_deduction: {
    points_cost: number
    remaining_points: number
    execution_time: number
    timestamp: number
  }
}

/** Dictionary of all api calls */
interface ApiCalls extends BackendApiCalls, FrontendApiCalls {}

/** Used to create a discriminating union on type value. */
interface ApiMessage<T extends keyof ApiCalls> {
  type: T
  data: ApiCalls[T]
}

export class UnauthorizedError extends Error {}

/** Ensures workers get a fair shake. */
type Unionize<T> = T[keyof T]

/**
 *  Discriminated union of generic, i.e.:
 * ```ts
 * // Convert
 * type ApiMessageUnion = ApiMessage<'status' | 'executing' | ...>
 * // To
 * type ApiMessageUnion = ApiMessage<'status'> | ApiMessage<'executing'> | ...
 * ```
 */
type ApiMessageUnion = Unionize<{
  [Key in keyof ApiCalls]: ApiMessage<Key>
}>

/** Wraps all properties in {@link CustomEvent}. */
type AsCustomEvents<T> = {
  readonly [K in keyof T]: CustomEvent<T[K]>
}

/** Handles differing event and API signatures. */
type ApiToEventType<T = ApiCalls> = {
  [K in keyof T]: K extends 'status'
    ? StatusWsMessageStatus
    : K extends 'executing'
      ? NodeId
      : T[K]
}

/** Dictionary of types used in the detail for a custom event */
type ApiEventTypes = ApiToEventType<ApiCalls>

/** Dictionary of API events: `[name]: CustomEvent<Type>` */
type ApiEvents = AsCustomEvents<ApiEventTypes>

/** {@link Omit} all properties that evaluate to `never`. */
type NeverNever<T> = {
  [K in keyof T as T[K] extends never ? never : K]: T[K]
}

/** {@link Pick} only properties that evaluate to `never`. */
type PickNevers<T> = {
  [K in keyof T as T[K] extends never ? K : never]: T[K]
}

/** Keys (names) of API events that _do not_ pass a {@link CustomEvent} `detail` object. */
type SimpleApiEvents = keyof PickNevers<ApiEventTypes>
/** Keys (names) of API events that pass a {@link CustomEvent} `detail` object. */
type ComplexApiEvents = keyof NeverNever<ApiEventTypes>

/** EventTarget typing has no generic capability. */
export interface ComfyApi extends EventTarget {
  addEventListener<TEvent extends keyof ApiEvents>(
    type: TEvent,
    callback: ((event: ApiEvents[TEvent]) => void) | null,
    options?: AddEventListenerOptions | boolean
  ): void

  removeEventListener<TEvent extends keyof ApiEvents>(
    type: TEvent,
    callback: ((event: ApiEvents[TEvent]) => void) | null,
    options?: EventListenerOptions | boolean
  ): void
}

export class PromptExecutionError extends Error {
  response: PromptResponse

  constructor(response: PromptResponse) {
    super('Prompt execution failed')
    this.response = response
  }

  override toString() {
    let message = ''
    if (typeof this.response.error === 'string') {
      message += this.response.error
    } else if (this.response.error) {
      message +=
        this.response.error.message + ': ' + this.response.error.details
    }

    for (const [_, nodeError] of Object.entries(
      this.response.node_errors ?? []
    )) {
      message += '\n' + nodeError.class_type + ':'
      for (const errorReason of nodeError.errors) {
        message += '\n    - ' + errorReason.message + ': ' + errorReason.details
      }
    }

    return message
  }
}

export class ComfyApi extends EventTarget {
  #registered = new Set()
  api_host: string
  api_base: string
  /**
   * The client id from the initial session storage.
   */
  initialClientId: string | null
  /**
   * The current client id from websocket status updates.
   */
  clientId?: string
  /**
   * The current user id.
   */
  user: string
  socket: WebSocket | null = null

  reportedUnknownMessageTypes = new Set<string>()

  /**
   * Get feature flags supported by this frontend client.
   * Returns a copy to prevent external modification.
   */
  getClientFeatureFlags(): Record<string, unknown> {
    return { ...defaultClientFeatureFlags }
  }

  /**
   * Feature flags received from the backend server.
   */
  serverFeatureFlags: Record<string, unknown> = {}

  /**
   * The auth token for the comfy org account if the user is logged in.
   * This is only used for {@link queuePrompt} now. It is not directly
   * passed as parameter to the function because some custom nodes are hijacking
   * {@link queuePrompt} improperly, which causes extra parameters to be lost
   * in the function call chain.
   *
   * Ref: https://cs.comfy.org/search?q=context:global+%22api.queuePrompt+%3D%22&patternType=keyword&sm=0
   *
   * Move this field to parameter of {@link queuePrompt} once all
   * custom nodes are patched.
   */
  authToken?: string
  /**
   * The API key for the comfy org account if the user logged in via API key.
   */
  apiKey?: string

  constructor() {
    super()
    this.user = ''
    this.api_host = location.host
    this.api_base = location.pathname.split('/').slice(0, -1).join('/')
    console.log('Running on', this.api_host)
    this.initialClientId = sessionStorage.getItem('clientId')
  }

  internalURL(route: string): string {
    return this.api_base + '/internal' + route
  }

  apiURL(route: string): string {
    // Use different API prefixes based on route
    if (route.startsWith('/sign/')) {
      return (
        FASTAPI_CONFIG.BASE_URL + '/api/v1/sign' + route.replace('/sign', '')
      )
    }
    // Use shares API for shares routes
    if (route.startsWith('/shares/')) {
      return FASTAPI_CONFIG.BASE_URL + '/api/v1' + route
    }
    // Use tags API for tags routes
    if (route.startsWith('/tags/')) {
      return FASTAPI_CONFIG.BASE_URL + '/api/v1' + route
    }
    // Use workflow API for userdata routes
    if (route.startsWith('/userdata')) {
      return FASTAPI_CONFIG.BASE_URL + '/api/v1/workflow' + route
    }
    // Use workflow-apps API for workflow-apps routes
    if (route.startsWith('/workflow-apps/')) {
      return FASTAPI_CONFIG.BASE_URL + '/api/v1' + route
    }
    // Use progress API for progress routes
    if (route.startsWith('/progress/')) {
      return FASTAPI_CONFIG.BASE_URL + '/api/v1' + route
    }
    // Use FastAPI proxy for all other API calls
    return FASTAPI_CONFIG.BASE_URL + '/api/v1/comfyui' + route
  }

  fileURL(route: string): string {
    return this.api_base + route
  }

  /**
   * 获取图片代理 URL，用于解决跨域。传入完整 http(s) URL，返回后端代理地址。
   * @param imageUrl 完整图片 URL（http/https）
   */
  viewProxyURL(imageUrl: string): string {
    return this.apiURL(
      '/view?' + new URLSearchParams({ url: imageUrl }).toString()
    )
  }

  async getAuthToken(): Promise<string | null> {
    // Get token from authStore
    try {
      // Import authStore dynamically to avoid circular dependencies
      const authStoreModule = await import('@/stores/authStore')
      const authStore = authStoreModule.useAuthStore()
      return authStore.getAccessToken()
    } catch (error) {
      console.warn('Failed to get auth token from authStore:', error)
      return null
    }
  }

  async fetchApi(route: string, options?: RequestInit) {
    if (!options) {
      options = {}
    }
    if (!options.headers) {
      options.headers = {}
    }
    if (!options.cache) {
      options.cache = 'no-cache'
    }

    // Add Comfy-User header
    if (Array.isArray(options.headers)) {
      options.headers.push(['Comfy-User', this.user])
    } else if (options.headers instanceof Headers) {
      options.headers.set('Comfy-User', this.user)
    } else {
      options.headers['Comfy-User'] = this.user
    }

    // Add authentication token for FastAPI proxy
    const token = await this.getAuthToken()
    if (token) {
      if (Array.isArray(options.headers)) {
        options.headers.push(['Authorization', `Bearer ${token}`])
      } else if (options.headers instanceof Headers) {
        options.headers.set('Authorization', `Bearer ${token}`)
      } else {
        options.headers['Authorization'] = `Bearer ${token}`
      }
    }

    // Add ComfyUI server preference for proxy routes (object_info, queue, get_image, etc.)
    try {
      const { useComfyUIServerStore } = await import(
        '@/stores/comfyuiServerStore'
      )
      const serverStore = useComfyUIServerStore()
      const serverId = serverStore.selectedServerId
      if (serverId != null) {
        const headerVal = String(serverId)
        if (Array.isArray(options.headers)) {
          options.headers.push(['X-ComfyUI-Server-Id', headerVal])
        } else if (options.headers instanceof Headers) {
          options.headers.set('X-ComfyUI-Server-Id', headerVal)
        } else {
          ;(options.headers as Record<string, string>)['X-ComfyUI-Server-Id'] =
            headerVal
        }
      }
    } catch {
      // Store may not be initialized yet
    }

    const response = await fetch(this.apiURL(route), options)

    // Handle 401 errors
    if (response.status === 401) {
      // Check if we're in initialization phase (no user data yet)
      try {
        const authStoreModule = await import('@/stores/authStore')
        const authStore = authStoreModule.useAuthStore()

        // If user is not initialized yet, don't show dialog - let router guard handle redirect
        if (!authStore.isInitialized) {
          console.log(
            'API: 401 error during initialization, will redirect to login page'
          )
          return response
        }

        // 精简模式 /workflow2：LayoutDefault 已显示登录弹窗，不再重复弹出
        if (
          typeof window !== 'undefined' &&
          window.location.pathname.startsWith('/workflow2')
        ) {
          return response
        }

        // Show login dialog for users who need to authenticate
        const { useDialogService } = await import('@/services/dialogService')
        const dialogService = useDialogService()

        console.log('需要登录，显示登录弹窗')
        await dialogService.showSignInDialog()
      } catch (dialogError) {
        console.warn('Failed to show login dialog:', dialogError)
      }
    }

    return response
  }

  override addEventListener<TEvent extends keyof ApiEvents>(
    type: TEvent,
    callback: ((event: ApiEvents[TEvent]) => void) | null,
    options?: AddEventListenerOptions | boolean
  ) {
    // Type assertion: strictFunctionTypes.  So long as we emit events in a type-safe fashion, this is safe.
    super.addEventListener(type, callback as EventListener, options)
    this.#registered.add(type)
  }

  override removeEventListener<TEvent extends keyof ApiEvents>(
    type: TEvent,
    callback: ((event: ApiEvents[TEvent]) => void) | null,
    options?: EventListenerOptions | boolean
  ): void {
    super.removeEventListener(type, callback as EventListener, options)
  }

  /**
   * Dispatches a custom event.
   * Provides type safety for the contravariance issue with EventTarget (last checked TS 5.6).
   * @param type The type of event to emit
   * @param detail The detail property used for a custom event ({@link CustomEventInit.detail})
   */
  dispatchCustomEvent<T extends SimpleApiEvents>(type: T): boolean
  dispatchCustomEvent<T extends ComplexApiEvents>(
    type: T,
    detail: ApiEventTypes[T] | null
  ): boolean
  dispatchCustomEvent<T extends keyof ApiEventTypes>(
    type: T,
    detail?: ApiEventTypes[T]
  ): boolean {
    const event =
      detail === undefined
        ? new CustomEvent(type)
        : new CustomEvent(type, { detail })
    return super.dispatchEvent(event)
  }

  /** @deprecated Use {@link dispatchCustomEvent}. */
  override dispatchEvent(event: never): boolean {
    return super.dispatchEvent(event)
  }

  /**
   * Poll status  for colab and other things that don't support websockets.
   */
  #pollQueue() {
    setInterval(async () => {
      try {
        const resp = await this.fetchApi('/prompt')
        const status = (await resp.json()) as StatusWsMessageStatus
        this.dispatchCustomEvent('status', status)
      } catch (error) {
        this.dispatchCustomEvent('status', null)
      }
    }, 1000)
  }

  /**
   * Creates and connects a WebSocket for realtime updates
   * @param {boolean} isReconnect If the socket is connection is a reconnect attempt
   */

  async #createSocket(isReconnect?: boolean) {
    if (this.socket) {
      return
    }

    if (await this.#shouldSkipConnection()) {
      this.dispatchCustomEvent('status', null)
      return
    }

    let opened = false
    let existingSession = window.name
    if (existingSession) {
      existingSession = '?clientId=' + existingSession
    }
    // Use FastAPI WebSocket URL instead of current host
    const wsBaseUrl = FASTAPI_CONFIG.BASE_URL.replace(
      'http://',
      'ws://'
    ).replace('https://', 'wss://')

    // Get authentication token
    const token = await this.getAuthToken()

    // Build query parameters
    const params = new URLSearchParams()
    if (existingSession) {
      params.append('clientId', window.name)
    }
    if (token) {
      params.append('token', token)
    }

    const queryString = params.toString()
    const wsUrl = `${wsBaseUrl}/ws${queryString ? '?' + queryString : ''}`

    this.socket = new WebSocket(wsUrl)
    this.socket.binaryType = 'arraybuffer'

    this.socket.addEventListener('open', () => {
      opened = true
      console.log('WebSocket connection opened')

      // Send feature flags as the first message
      this.socket!.send(
        JSON.stringify({
          type: 'feature_flags',
          data: this.getClientFeatureFlags()
        })
      )

      if (isReconnect) {
        this.dispatchCustomEvent('reconnected')
      }
    })

    this.socket.addEventListener('error', async () => {
      if (this.socket) this.socket.close()
      if (!isReconnect && !opened) {
        if (!(await this.#shouldSkipConnection())) {
          this.#pollQueue()
        }
      }
    })

    this.socket.addEventListener('close', async (event) => {
      // 检查是否是认证错误（401或403）
      if (event.code === 1008 || event.code === 1002) {
        // POLICY_VIOLATION 或 PROTOCOL_ERROR
        console.log(
          'WebSocket closed due to authentication error, attempting to refresh token...'
        )

        try {
          // 尝试刷新token
          // Token refresh temporarily disabled
          console.log('Token refresh temporarily disabled')
          const refreshed = false
          /*
          const authStoreModule = await import('@/stores/authStore')
          const authStore = authStoreModule.useAuthStore()
          */
          // const refreshed = await authStore.refreshAccessToken()

          if (refreshed) {
            console.log(
              'Token refreshed successfully, reconnecting WebSocket...'
            )
            // 延迟重连，给token刷新一些时间
            setTimeout(async () => {
              this.socket = null
              await this.#createSocket(true)
            }, 1000)
            return
          } else {
            console.log('Token refresh failed, showing login dialog...')
            // Token刷新失败，显示登录对话框
            try {
              // Show toast notification first
              const { useToastStore } = await import(
                '@/platform/updates/common/toastStore'
              )
              const toastStore = useToastStore()
              toastStore.add({
                severity: 'warn',
                summary: '需要登录',
                detail: '您的登录已过期，请重新登录',
                life: 3000
              })

              // 精简模式 /workflow2 已显示登录弹窗，不重定向避免循环刷新
              if (!window.location.pathname.startsWith('/workflow2')) {
                console.log('登录已过期，直接跳转到登录页面')
                window.location.href = '/login'
              }
            } catch (dialogError) {
              console.warn('Failed to show login dialog:', dialogError)
            }
            return
          }
        } catch (error) {
          console.error('Error refreshing token:', error)
          // 刷新失败，显示登录对话框
          try {
            // Show toast notification first
            const { useToastStore } = await import(
              '@/platform/updates/common/toastStore'
            )
            const toastStore = useToastStore()
            toastStore.add({
              severity: 'warn',
              summary: '需要登录',
              detail: '您的登录已过期，请重新登录',
              life: 3000
            })

            // 精简模式 /workflow2 已显示登录弹窗，不重定向避免循环刷新
            if (!window.location.pathname.startsWith('/workflow2')) {
              console.log('登录已过期，直接跳转到登录页面')
              window.location.href = '/login'
            }
          } catch (dialogError) {
            console.warn('Failed to show login dialog:', dialogError)
          }
          return
        }
      }

      // 正常的重连逻辑（workflow2 未登录时不再重连，避免循环）
      setTimeout(async () => {
        this.socket = null
        if (!(await this.#shouldSkipConnection())) {
          await this.#createSocket(true)
        }
      }, 300)
      if (opened) {
        this.dispatchCustomEvent('status', null)
        this.dispatchCustomEvent('reconnecting')
      }
    })

    this.socket.addEventListener('message', (event) => {
      console.log('WebSocket message received:', event.data)
      try {
        if (event.data instanceof ArrayBuffer) {
          const view = new DataView(event.data)
          const eventType = view.getUint32(0)

          let imageMime
          switch (eventType) {
            case 3:
              const decoder = new TextDecoder()
              const data = event.data.slice(4)
              const nodeIdLength = view.getUint32(4)
              this.dispatchCustomEvent('progress_text', {
                nodeId: decoder.decode(data.slice(4, 4 + nodeIdLength)),
                text: decoder.decode(data.slice(4 + nodeIdLength))
              })
              break
            case 1:
              const imageType = view.getUint32(4)
              const imageData = event.data.slice(8)
              switch (imageType) {
                case 2:
                  imageMime = 'image/png'
                  break
                case 1:
                default:
                  imageMime = 'image/jpeg'
                  break
              }
              const imageBlob = new Blob([imageData], {
                type: imageMime
              })
              this.dispatchCustomEvent('b_preview', imageBlob)
              break
            case 4:
              // PREVIEW_IMAGE_WITH_METADATA
              const decoder4 = new TextDecoder()
              const metadataLength = view.getUint32(4)
              const metadataBytes = event.data.slice(8, 8 + metadataLength)
              const metadata = JSON.parse(decoder4.decode(metadataBytes))
              const imageData4 = event.data.slice(8 + metadataLength)

              let imageMime4 = metadata.image_type

              const imageBlob4 = new Blob([imageData4], {
                type: imageMime4
              })

              // Dispatch enhanced preview event with metadata
              this.dispatchCustomEvent('b_preview_with_metadata', {
                blob: imageBlob4,
                nodeId: metadata.node_id,
                displayNodeId: metadata.display_node_id,
                parentNodeId: metadata.parent_node_id,
                realNodeId: metadata.real_node_id,
                promptId: metadata.prompt_id
              })

              // Also dispatch legacy b_preview for backward compatibility
              this.dispatchCustomEvent('b_preview', imageBlob4)
              break
            default:
              throw new Error(
                `Unknown binary websocket message of type ${eventType}`
              )
          }
        } else {
          const msg = JSON.parse(event.data) as ApiMessageUnion
          switch (msg.type) {
            case 'status':
              console.log('Received status message:', msg)
              console.log('Status message data:', msg.data)
              console.log('Status message status:', msg.data.status)
              if (msg.data.sid) {
                const clientId = msg.data.sid
                this.clientId = clientId
                window.name = clientId // use window name so it isnt reused when duplicating tabs
                sessionStorage.setItem('clientId', clientId) // store in session storage so duplicate tab can load correct workflow
              }
              console.log(
                'Dispatching status event with data:',
                msg.data.status
              )
              this.dispatchCustomEvent('status', msg.data.status ?? null)
              break
            case 'executing':
              this.dispatchCustomEvent(
                'executing',
                msg.data.display_node || msg.data.node
              )
              break
            case 'execution_start':
            case 'execution_error':
            case 'execution_interrupted':
            case 'execution_cached':
            case 'execution_success':
            case 'progress':
            case 'progress_state':
            case 'executed':
            case 'graphChanged':
            case 'promptQueued':
            case 'logs':
            case 'b_preview':
            case 'task_error':
              this.dispatchCustomEvent(msg.type, msg.data)
              break
            case 'feature_flags':
              // Store server feature flags
              this.serverFeatureFlags = msg.data
              console.log(
                'Server feature flags received:',
                this.serverFeatureFlags
              )
              break
            case 'points_deduction':
              console.log('Received points deduction message:', msg.data)
              this.dispatchCustomEvent('points_deduction', msg.data)
              break
            default:
              if (this.#registered.has(msg.type)) {
                // Fallback for custom types - calls super direct.
                super.dispatchEvent(
                  new CustomEvent(msg.type, { detail: msg.data })
                )
              } else if (!this.reportedUnknownMessageTypes.has(msg.type)) {
                this.reportedUnknownMessageTypes.add(msg.type)
                throw new Error(`Unknown message type ${msg.type}`)
              }
          }
        }
      } catch (error) {
        console.warn('Unhandled message:', event.data, error)
      }
    })
  }

  /**
   * 精简模式 /workflow2 下未登录时是否应跳过连接（避免无限重连/轮询）
   */
  async #shouldSkipConnection(): Promise<boolean> {
    if (typeof window === 'undefined') return false
    if (!window.location.pathname.startsWith('/workflow2')) return false
    try {
      const authStoreModule = await import('@/stores/authStore')
      const authStore = authStoreModule.useAuthStore()
      return authStore.isInitialized && !authStore.isAuthenticated
    } catch {
      return false
    }
  }

  /**
   * Initialises sockets and realtime updates
   */
  async init() {
    if (await this.#shouldSkipConnection()) {
      console.log('API: workflow2 未登录，跳过 WebSocket 连接避免反复重试')
      this.dispatchCustomEvent('status', null)
      return
    }
    await this.#createSocket()
  }

  /**
   * Gets a list of extension urls
   */
  async getExtensions(): Promise<ExtensionsResponse> {
    const resp = await this.fetchApi('/extensions', { cache: 'no-store' })
    return await resp.json()
  }

  /**
   * Gets the available workflow templates from custom nodes.
   * @returns A map of custom_node names and associated template workflow names.
   */
  async getWorkflowTemplates(): Promise<{
    [customNodesName: string]: string[]
  }> {
    const res = await this.fetchApi('/workflow_templates')
    return await res.json()
  }

  /**
   * Gets the index of core workflow templates.
   */
  async getCoreWorkflowTemplates(): Promise<WorkflowTemplates[]> {
    const res = await axios.get(this.fileURL('/templates/index.json'))
    const contentType = res.headers['content-type']
    return contentType?.includes('application/json') ? res.data : []
  }

  /**
   * Gets a list of embedding names
   */
  async getEmbeddings(): Promise<EmbeddingsResponse> {
    const resp = await this.fetchApi('/embeddings', { cache: 'no-store' })
    return await resp.json()
  }

  /**
   * Loads node object definitions for the graph
   * @returns The node definitions
   */
  async getNodeDefs(): Promise<Record<string, ComfyNodeDef>> {
    const resp = await this.fetchApi('/object_info', { cache: 'no-store' })
    return await resp.json()
  }

  /**
   * Queues a prompt to be executed
   * @param {number} number The index at which to queue the prompt, passing -1 will insert the prompt at the front of the queue
   * @param {object} data The prompt data to queue
   * @param {QueuePromptOptions} options Optional execution options
   * @throws {PromptExecutionError} If the prompt fails to execute
   */
  async queuePrompt(
    number: number,
    data: { output: ComfyApiWorkflow; workflow: ComfyWorkflowJSON },
    options?: QueuePromptOptions & {
      required_capabilities?: string[]
      workflows_id?: number
    }
  ): Promise<PromptResponse> {
    const { output: prompt, workflow } = data

    const body: QueuePromptRequestBody = {
      client_id: this.clientId ?? '', // Unify clientId access
      prompt,
      ...(options?.partialExecutionTargets && {
        partial_execution_targets: options.partialExecutionTargets
      }),
      ...(options?.required_capabilities && {
        required_capabilities: options.required_capabilities
      }),
      ...(options?.workflows_id && {
        workflows_id: options.workflows_id
      }),
      extra_data: {
        auth_token_comfy_org: this.authToken,
        api_key_comfy_org: this.apiKey,
        extra_pnginfo: {
          workflow: workflow // workflowApp现在直接包含在workflow中
        }
      }
    }

    if (number === -1) {
      body.front = true
    } else if (number != 0) {
      body.number = number
    }

    const res = await this.fetchApi('/prompt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    const responseData = await res.json()

    // 检查是否是429错误（通过状态码或错误消息内容判断）
    const isTooManyRequests =
      res.status === 429 ||
      responseData.detail?.includes('您当前有正在执行的 prompt') ||
      responseData.error?.message?.includes('您当前有正在执行的 prompt')

    if (isTooManyRequests) {
      const toastStore = useToastStore()
      toastStore.add({
        severity: 'warn',
        summary: '提示',
        detail:
          responseData.detail ||
          '您当前有正在执行的 prompt，请等待完成后再提交新的任务',
        life: 5000
      })
      // 返回一个特殊的响应，避免抛出异常
      return { error: 'too_many_requests' } as PromptResponse
    }

    // 处理200状态但包含错误的情况
    if (res.status === 200 && responseData.error) {
      const toastStore = useToastStore()
      toastStore.add({
        severity: 'error',
        summary: '验证失败',
        detail:
          responseData.error.message || 'Prompt outputs failed validation',
        life: 5000
      })
      throw new PromptExecutionError(responseData)
    }

    if (res.status !== 200) {
      throw new PromptExecutionError(responseData)
    }

    return responseData
  }

  /**
   * Gets a list of model folder keys (eg ['checkpoints', 'loras', ...])
   * @returns The list of model folder keys
   */
  async getModelFolders(): Promise<ModelFolderInfo[]> {
    const res = await this.fetchApi(`/experiment/models`)
    if (res.status === 404) {
      return []
    }

    try {
      const data = await res.json()

      // 确保返回的数据是数组
      if (!Array.isArray(data)) {
        console.warn('getModelFolders: API returned non-array data:', data)
        return []
      }

      const folderBlacklist = ['configs', 'custom_nodes']
      return data.filter(
        (folder: ModelFolderInfo) => !folderBlacklist.includes(folder.name)
      )
    } catch (error) {
      console.error('getModelFolders: Failed to parse response:', error)
      return []
    }
  }

  /**
   * Gets a list of models in the specified folder
   * @param {string} folder The folder to list models from, such as 'checkpoints'
   * @returns The list of model filenames within the specified folder
   */
  async getModels(folder: string): Promise<ModelFile[]> {
    const res = await this.fetchApi(`/experiment/models/${folder}`)
    if (res.status === 404) {
      return []
    }
    return await res.json()
  }

  /**
   * Gets the metadata for a model
   * @param {string} folder The folder containing the model
   * @param {string} model The model to get metadata for
   * @returns The metadata for the model
   */
  async viewMetadata(folder: string, model: string) {
    const res = await this.fetchApi(
      `/view_metadata/${folder}?filename=${encodeURIComponent(model)}`
    )
    const rawResponse = await res.text()
    if (!rawResponse) {
      return null
    }
    try {
      return JSON.parse(rawResponse)
    } catch (error) {
      console.error(
        'Error viewing metadata',
        res.status,
        res.statusText,
        rawResponse,
        error
      )
      return null
    }
  }

  /**
   * Loads a list of items (queue or history)
   * @param {string} type The type of items to load, queue or history
   * @returns The items of the specified type grouped by their status
   */
  async getItems(type: 'queue' | 'history') {
    if (type === 'queue') {
      return this.getQueue()
    }
    return this.getHistory()
  }

  /**
   * Gets the current state of the queue
   * @returns The currently running and queued items
   */
  async getQueue(): Promise<{
    Running: RunningTaskItem[]
    Pending: PendingTaskItem[]
  }> {
    try {
      const res = await this.fetchApi('/queue')
      const data = await res.json()

      // Ensure queue_running and queue_pending are arrays
      const queueRunning = Array.isArray(data.queue_running)
        ? data.queue_running
        : []
      const queuePending = Array.isArray(data.queue_pending)
        ? data.queue_pending
        : []

      return {
        // Running action uses a different endpoint for cancelling
        Running: queueRunning.map((prompt: Record<number, any>) => ({
          taskType: 'Running',
          prompt,
          // prompt[1] is the prompt id
          remove: { name: 'Cancel', cb: () => api.interrupt(prompt[1]) }
        })),
        Pending: queuePending.map((prompt: Record<number, any>) => ({
          taskType: 'Pending',
          prompt
        }))
      }
    } catch (error) {
      console.error(error)
      return { Running: [], Pending: [] }
    }
  }

  /**
   * Gets the list of server capabilities for workflow run (aggregated from all servers)
   * @returns List of capability strings e.g. ['24G', '48G', '24G/48G']
   */
  async getServerCapabilities(): Promise<string[]> {
    try {
      const res = await this.fetchApi('/capabilities')
      const data = await res.json()
      return Array.isArray(data?.capabilities) ? data.capabilities : []
    } catch (error) {
      console.error('获取服务器能力列表失败:', error)
      return []
    }
  }

  /**
   * Gets the queue status with user queue position for polling
   * @returns Queue status including user queue position
   */
  async getQueueStatus(): Promise<{
    queue_running: any[]
    queue_pending: any[]
    user_queue_position: number | null
  }> {
    try {
      const res = await this.fetchApi('/queue')
      const data = await res.json()

      return {
        queue_running: Array.isArray(data.queue_running)
          ? data.queue_running
          : [],
        queue_pending: Array.isArray(data.queue_pending)
          ? data.queue_pending
          : [],
        user_queue_position: data.user_queue_position || null
      }
    } catch (error) {
      console.error('获取队列状态失败:', error)
      return {
        queue_running: [],
        queue_pending: [],
        user_queue_position: null
      }
    }
  }

  /**
   * Gets the prompt execution history
   * @returns Prompt history including node outputs
   */
  async getHistory(
    max_items?: number,
    offset?: number,
    exclude_no_output?: boolean,
    page?: number,
    page_size?: number
  ): Promise<{ History: HistoryTaskItem[]; pagination?: any }> {
    try {
      const params = new URLSearchParams()

      // 如果提供了分页参数，使用新的分页方式
      if (page !== undefined && page_size !== undefined) {
        params.set('page', String(page))
        params.set('page_size', String(page_size))
      } else {
        // 否则使用旧的参数（向后兼容）
        if (max_items !== undefined) {
          params.set('max_items', String(max_items))
        }
        if (offset !== undefined) {
          params.set('offset', String(offset))
        }
      }

      if (exclude_no_output !== undefined && exclude_no_output) {
        params.set('exclude_no_output', 'true')
      }
      const queryString = params.toString()
      const res = await this.fetchApi(
        `/history${queryString ? `?${queryString}` : ''}`
      )
      const json = await res.json()

      // 检查是否是新的分页格式
      if (json.data && json.pagination) {
        // 新格式：{ data: { prompt_id: history_item }, pagination: {...} }
        return {
          History: Object.values(json.data).map((item: any) => ({
            ...item,
            taskType: 'History'
          })),
          pagination: json.pagination
        }
      } else {
        // 旧格式：{ prompt_id: history_item }
        return {
          History: Object.values(json).map((item: any) => ({
            ...item,
            taskType: 'History'
          }))
        }
      }
    } catch (error) {
      console.error(error)
      return { History: [] }
    }
  }

  /**
   * Gets system & device stats
   * @returns System stats such as python version, OS, per device info
   */
  // async getSystemStats(): Promise<SystemStats> {
  //   const res = await this.fetchApi('/system_stats')
  //   return await res.json()
  // }

  /**
   * Gets the list of available ComfyUI servers
   * @returns List of ComfyUI servers with their status and information
   */
  async getServers(): Promise<ServersResponse> {
    const res = await this.fetchApi('/servers')
    if (!res.ok) {
      throw new Error(`获取服务器列表失败: ${res.status} ${res.statusText}`)
    }
    const data = await res.json()
    return {
      servers: data.servers.map((server: any) => ({
        ...server,
        monitorData: undefined,
        lastUpdate: undefined
      }))
    }
  }

  /**
   * Gets monitoring data for a specific server
   * @param serverId The ID of the server to get monitoring data for
   * @returns Server monitoring data
   */
  async getServerMonitorData(
    serverId: number
  ): Promise<{ has_data: boolean; monitor_message?: any }> {
    const res = await this.fetchApi(`/progress/server-monitor/${serverId}`)
    if (!res.ok) {
      throw new Error(
        `获取服务器 ${serverId} 监控数据失败: ${res.status} ${res.statusText}`
      )
    }
    return await res.json()
  }

  /**
   * Updates server information
   * @param serverId The ID of the server to update
   * @param data The server data to update
   * @returns Update result
   */
  async updateServer(serverId: number, data: any): Promise<any> {
    const res = await this.fetchApi(`/servers/${serverId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    if (!res.ok) {
      throw new Error(`更新服务器失败: ${res.status} ${res.statusText}`)
    }
    return await res.json()
  }

  /**
   * Creates a new server
   * @param data The server data to create
   * @returns Create result
   */
  async createServer(data: any): Promise<any> {
    const res = await this.fetchApi('/servers', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    if (!res.ok) {
      throw new Error(`创建服务器失败: ${res.status} ${res.statusText}`)
    }
    return await res.json()
  }

  /**
   * Deletes a server
   * @param serverId The ID of the server to delete
   * @returns Delete result
   */
  async deleteServer(serverId: number): Promise<any> {
    const res = await this.fetchApi(`/servers/${serverId}`, {
      method: 'DELETE'
    })
    if (!res.ok) {
      throw new Error(`删除服务器失败: ${res.status} ${res.statusText}`)
    }
    return await res.json()
  }

  /**
   * Sends a POST request to the API
   * @param {*} type The endpoint to post to
   * @param {*} body Optional POST data
   */
  async #postItem(type: string, body: any) {
    try {
      await this.fetchApi('/' + type, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: body ? JSON.stringify(body) : undefined
      })
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Deletes an item from the specified list
   * @param {string} type The type of item to delete, queue or history
   * @param {number} id The id of the item to delete
   */
  async deleteItem(type: string, id: string) {
    await this.#postItem(type, { delete: [id] })
  }

  /**
   * Clears the specified list
   * @param {string} type The type of list to clear, queue or history
   */
  async clearItems(type: string) {
    await this.#postItem(type, { clear: true })
  }

  /**
   * Interrupts the execution of the running prompt. If runningPromptId is provided,
   * it is included in the payload as a helpful hint to the backend.
   * @param {string | null} [runningPromptId] Optional Running Prompt ID to interrupt
   */
  async interrupt(runningPromptId: string | null) {
    await this.#postItem(
      'interrupt',
      runningPromptId ? { prompt_id: runningPromptId } : undefined
    )
  }

  /**
   * Gets user configuration data and where data should be stored
   */
  async getUserConfig(): Promise<User> {
    // return (await this.fetchApi('/users')).json()
    // Return default configuration instead of making API request
    return {
      storage: 'server',
      migrated: true
    }
  }

  /**
   * Creates a new user
   * @param { string } username
   * @returns The fetch response
   */
  async createUser(username: string) {
    return this.fetchApi('/users', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username })
    })
  }

  /**
   * Gets all setting values for the current user
   * @returns { Promise<string, unknown> } A dictionary of id -> value
   */
  async getSettings(): Promise<Settings> {
    const resp = await this.fetchApi('/settings')

    if (resp.status == 401) {
      throw new UnauthorizedError(resp.statusText)
    }
    return await resp.json()
  }

  /**
   * Gets a setting for the current user
   * @param { string } id The id of the setting to fetch
   * @returns { Promise<unknown> } The setting value
   */
  async getSetting(id: keyof Settings): Promise<Settings[keyof Settings]> {
    return (await this.fetchApi(`/settings/${encodeURIComponent(id)}`)).json()
  }

  /**
   * Stores a dictionary of settings for the current user
   */
  async storeSettings(settings: Settings) {
    return this.fetchApi(`/settings`, {
      method: 'POST',
      body: JSON.stringify(settings)
    })
  }

  /**
   * Stores a setting for the current user
   */
  async storeSetting(id: keyof Settings, value: Settings[keyof Settings]) {
    return this.fetchApi(`/settings/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify(value)
    })
  }

  /**
   * Gets a user data file for the current user
   */
  async getUserData(file: string, options?: RequestInit) {
    return this.fetchApi(`/userdata/${encodeURIComponent(file)}`, options)
  }

  /**
   * Stores a user data file for the current user
   * @param { string } file The name of the userdata file to save
   * @param { unknown } data The data to save to the file
   * @param { RequestInit & { stringify?: boolean, throwOnError?: boolean } } [options]
   * @returns { Promise<Response> }
   */
  async storeUserData(
    file: string,
    data: any,
    options: RequestInit & {
      overwrite?: boolean
      stringify?: boolean
      throwOnError?: boolean
      full_info?: boolean
    } = {
      overwrite: true,
      stringify: true,
      throwOnError: true,
      full_info: false
    }
  ): Promise<Response> {
    const resp = await this.fetchApi(
      `/userdata/${encodeURIComponent(file)}?overwrite=${options.overwrite}&full_info=${options.full_info}`,
      {
        method: 'POST',
        body: options?.stringify ? JSON.stringify(data) : data,
        ...options
      }
    )
    if (resp.status !== 200 && options.throwOnError !== false) {
      throw new Error(
        `Error storing user data file '${file}': ${resp.status} ${(await resp).statusText}`
      )
    }

    return resp
  }

  /**
   * Deletes a user data file for the current user
   * @param { string } file The name of the userdata file to delete
   */
  async deleteUserData(file: string) {
    const resp = await this.fetchApi(`/userdata/${encodeURIComponent(file)}`, {
      method: 'DELETE'
    })
    return resp
  }

  /**
   * Move a user data file for the current user
   * @param { string } source The userdata file to move
   * @param { string } dest The destination for the file
   */
  async moveUserData(
    source: string,
    dest: string,
    options = { overwrite: false }
  ) {
    const resp = await this.fetchApi(
      `/userdata/${encodeURIComponent(source)}/move/${encodeURIComponent(dest)}?overwrite=${options?.overwrite}`,
      {
        method: 'POST'
      }
    )
    return resp
  }

  async listUserDataFullInfo(dir: string): Promise<UserDataFullInfo[]> {
    const resp = await this.fetchApi(
      `/userdata?dir=${encodeURIComponent(dir)}&recurse=true&split=false&full_info=true`
    )
    if (resp.status === 404) return []
    if (resp.status !== 200) {
      throw new Error(
        `Error getting user data list '${dir}': ${resp.status} ${resp.statusText}`
      )
    }
    return resp.json()
  }

  async getLogs(): Promise<string> {
    // 在429错误时不请求logs
    // Temporarily disabled - logs interface commented out
    console.log('getLogs() temporarily disabled')
    return ''
    /*
    try {
      return (await axios.get(this.internalURL('/logs'))).data
    } catch (error: any) {
      if (error.response?.status === 429) {
        return ''
      }
      throw error
    }
    */
  }

  async getRawLogs(): Promise<LogsRawResponse> {
    // 在429错误时不请求logs
    // Temporarily disabled - logs interface commented out
    console.log('getRawLogs() temporarily disabled')
    return { entries: [], size: { cols: 0, row: 0 } }
    /*
    try {
      return (await axios.get(this.internalURL('/logs/raw'))).data
    } catch (error: any) {
      if (error.response?.status === 429) {
        return { entries: [], size: { cols: 0, row: 0 } }
      }
      throw error
    }
    */
  }

  async subscribeLogs(_enabled: boolean): Promise<void> {
    // 在429错误时不订阅logs
    // Temporarily disabled - logs interface commented out
    console.log('subscribeLogs() temporarily disabled')
    return
    /*
    try {
      await axios.patch(this.internalURL('/logs/subscribe'), {
        enabled,
        clientId: this.clientId
      })
    } catch (error: any) {
      if (error.response?.status === 429) {
        return
      }
      throw error
    }
    */
  }

  async getFolderPaths(): Promise<Record<string, string[]>> {
    const response = await axios
      .get(this.internalURL('/folder_paths'))
      .catch(() => null)
    if (!response) {
      return {} // Fallback: no filesystem paths known when API unavailable
    }
    return response.data
  }

  /* Frees memory by unloading models and optionally freeing execution cache
   * @param {Object} options - The options object
   * @param {boolean} options.freeExecutionCache - If true, also frees execution cache
   */
  async freeMemory(options: { freeExecutionCache: boolean }) {
    try {
      let mode = ''
      if (options.freeExecutionCache) {
        mode = '{"unload_models": true, "free_memory": true}'
      } else {
        mode = '{"unload_models": true}'
      }

      const res = await this.fetchApi(`/free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: mode
      })

      if (res.status === 200) {
        if (options.freeExecutionCache) {
          useToastStore().add({
            severity: 'success',
            summary: 'Models and Execution Cache have been cleared.',
            life: 3000
          })
        } else {
          useToastStore().add({
            severity: 'success',
            summary: 'Models have been unloaded.',
            life: 3000
          })
        }
      } else {
        useToastStore().add({
          severity: 'error',
          summary:
            'Unloading of models failed. Installed ComfyUI may be an outdated version.',
          life: 5000
        })
      }
    } catch (error) {
      useToastStore().add({
        severity: 'error',
        summary: 'An error occurred while trying to unload models.',
        life: 5000
      })
    }
  }

  /**
   * Gets the custom nodes i18n data from the server.
   *
   * @returns The custom nodes i18n data
   */
  async getCustomNodesI18n(): Promise<Record<string, any>> {
    // Use the authenticated ComfyUI API service
    try {
      const { useComfyUIApiService } = await import(
        '@/services/comfyuiApiService'
      )
      const comfyuiApi = useComfyUIApiService()
      return await comfyuiApi.get('/i18n')
    } catch (error) {
      console.warn(
        'Failed to use ComfyUI API service, falling back to direct call:',
        error
      )
      // Fallback to direct call with token
      const token = this.getAuthToken()
      const headers: any = {}
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
      return (await axios.get(this.apiURL('/i18n'), { headers })).data
    }
  }

  /**
   * Checks if the server supports a specific feature.
   * @param featureName The name of the feature to check (supports dot notation for nested values)
   * @returns true if the feature is supported, false otherwise
   */
  serverSupportsFeature(featureName: string): boolean {
    return get(this.serverFeatureFlags, featureName) === true
  }

  /**
   * Gets a server feature flag value.
   * @param featureName The name of the feature to get (supports dot notation for nested values)
   * @param defaultValue The default value if the feature is not found
   * @returns The feature value or default
   */
  getServerFeature<T = unknown>(featureName: string, defaultValue?: T): T {
    return get(this.serverFeatureFlags, featureName, defaultValue) as T
  }

  /**
   * Gets all server feature flags.
   * @returns Copy of all server feature flags
   */
  getServerFeatures(): Record<string, unknown> {
    return { ...this.serverFeatureFlags }
  }

  /**
   * Daily signin to get points
   * @returns Signin result
   */
  async dailySignin(): Promise<{
    success: boolean
    message: string
    points_awarded?: number
    total_points?: number
    expired_points_deducted?: number
    is_first_signin_today: boolean
  }> {
    const token = this.getAuthToken()
    const headers: any = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return (
      await axios.post(this.apiURL('/sign/daily-signin'), {}, { headers })
    ).data
  }

  /**
   * Check today's signin status
   * @returns Signin status
   */
  async getSigninStatus(): Promise<{
    has_signed_in_today: boolean
    signin_date: string
    points_awarded?: number
  }> {
    const token = this.getAuthToken()
    const headers: any = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return (await axios.get(this.apiURL('/sign/signin-status'), { headers }))
      .data
  }

  /**
   * Get points history
   * @param limit Number of records to return
   * @param offset Number of records to skip
   * @returns Points history
   */
  async getPointsHistory(
    limit = 50,
    offset = 0
  ): Promise<{
    records: Array<{
      id: number
      points_change: number
      points_type: string
      description?: string
      expires_at?: string
      created_at: string
    }>
    total: number
  }> {
    const token = this.getAuthToken()
    const headers: any = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return (
      await axios.get(this.apiURL('/sign/points/history'), {
        headers,
        params: { limit, offset }
      })
    ).data
  }

  /**
   * Get signin history
   * @param limit Number of records to return
   * @param offset Number of records to skip
   * @returns Signin history
   */
  async getSigninHistory(
    limit = 30,
    offset = 0
  ): Promise<{
    records: Array<{
      id: number
      signin_date: string
      points_awarded: number
      created_at: string
    }>
    total: number
  }> {
    const token = this.getAuthToken()
    const headers: any = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return (
      await axios.get(this.apiURL('/sign/signin-history'), {
        headers,
        params: { limit, offset }
      })
    ).data
  }

  /**
   * Get points summary
   * @returns Points summary
   */
  async getPointsSummary(): Promise<{
    total_points: number
    expired_points: number
    available_points: number
    expiring_points: number
    expiring_soon: Array<{ points: number; expire_date: string }>
    today_consumed_points: number
  }> {
    const token = this.getAuthToken()
    const headers: any = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return (await axios.get(this.apiURL('/sign/points/summary'), { headers }))
      .data
  }

  /**
   * Download clean image without workflow metadata
   * @param imageUrl Image URL with query parameters
   * @returns Clean image blob
   */
  async downloadCleanImage(imageUrl: string): Promise<Blob> {
    const { useComfyUIApiService } = await import(
      '@/services/comfyuiApiService'
    )
    const comfyuiApi = useComfyUIApiService()

    const response = await comfyuiApi.get('/api/download-clean-image', {
      params: { url: imageUrl },
      responseType: 'blob'
    })

    return response.data
  }
}

export const api = new ComfyApi()
